import { createSlice } from '@reduxjs/toolkit'
import { RootState } from 'store'
import {
  getChallenge,
  uploadChallengeImage,
  createNewChallenge,
  updateChallenge,
  deleteChallenge,
  duplicateChallenge,
  getChallengeLeaderboardReportV1,
  addChallengePlayer,
  deleteChallengePlayer,
  updateChallengeState,
} from './thunks'
import { ChallengeInfoContentType } from 'utils/challenges'
import { getUserChallenge } from 'store/userChallenge/thunks'

const challenge: Challenge = {
  challengeId: '-1',
  token: '',
  controlledState: undefined,
  computedState: undefined,
  info: {
    name: '',
    subtitle: {
      type: ChallengeInfoContentType.CHALLENGE_INFO_CONTENT_TYPE_UNSPECIFIED,
      content: '',
    },
    longDescription: {
      type: ChallengeInfoContentType.CHALLENGE_INFO_CONTENT_TYPE_UNSPECIFIED,
      content: '',
    },
  },
  audienceControl: {
    isDiscoverable: true,
    useJoinAudienceOnly: false,
    joinAudience: {
      countries: [],
      countriesInclusive: true,
      birthdateFilter: {
        birthdateLowerLimit: undefined,
        birthdateUpperLimit: undefined,
      },
      hcpFilter: {
        minTenths: 0,
        maxTenths: 0,
      },
      gender: undefined,
      userSubscriptionTypes: [],
      privileges: [],
      clubIds: [],
    },
    extraViewAudience: {
      countries: [],
      countriesInclusive: true,
      birthdateFilter: {
        birthdateLowerLimit: undefined,
        birthdateUpperLimit: undefined,
      },
      hcpFilter: {
        minTenths: 0,
        maxTenths: 0,
      },
      gender: undefined,
      userSubscriptionTypes: [],
      privileges: [],
      clubIds: [],
    },
  },
  typeConfig: {
    type: 'CHALLENGE_TYPE_UNSPECIFIED',
    target: 0,
  },
  schedule: {
    visibleAt: '',
    startAt: '',
    endAt: '',
  },
  roundEligibility: {
    clubIds: [],
    courseIds: [],
    countries: [],
    countriesInclusive: true,
    minCoursePar: undefined,
    roundTypes: [],
  },
  media: {
    coverPreset: undefined,
    coverImageId: '',
    highlightImageId: '',
    bannerImageId: '',
    logoImageId: '',
    backgroundImageId: '',
    termsImageId: '',
  },
}

const initialState: ChallengeState = {
  challenge: challenge,
  status: 'idle',
  error: null,
  saveStatus: 'idle',
  saveAsDraftStatus: 'idle',
  updateStateStatus: 'idle',
  updateStateError: null,
  leaderboardStatus: 'idle',
  leaderboardError: null,
  redirectAfterCreate: false,
  fileUploadStatus: 'idle',
  fileUploadError: null,
  exportResultsStatus: 'idle',
  exportResultsError: null,
  deleteChallengePlayerStatus: 'idle',
  deleteChallengePlayerError: null,
  addChallengePlayerStatus: 'idle',
  addChallengePlayerError: null,
}

const slice = createSlice({
  name: 'challenge',
  initialState,
  reducers: {
    removeImage: (state, action) => {
      state.challenge[action.payload] = ''
    },
    clearChallenge: () => {
      return initialState
    },
    clearRedirect: (state) => {
      state.redirectAfterCreate = false
    },
    clearFileUploadStatus: (state) => {
      state.fileUploadStatus = 'idle'
    },
    clearFileUploadError: (state) => {
      state.fileUploadError = null
    },
    updateChallengeTypeConfig: (state, action) => {
      state.challenge.typeConfig = action.payload
    },
    updateChallengeInfo: (state, action) => {
      state.challenge.info = { ...state.challenge.info, ...action.payload }
    },
    updateChallengeField: (state, action: { payload: { path: string; value: any } }) => {
      const { path, value } = action.payload
      const keys = path.split('.')
      let obj: any = state.challenge
      for (let i = 0; i < keys.length - 1; i++) {
        obj = obj[keys[i]]
      }
      obj[keys[keys.length - 1]] = value
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getChallenge.pending, (state) => {
        state.status = 'loading'
        state.error = null
        state.saveStatus = 'idle'
        state.saveAsDraftStatus = 'idle'
        state.redirectAfterCreate = false
      })
      .addCase(getChallenge.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(getChallenge.fulfilled, (state, action) => {
        state.status = 'succeeded'
        if (!action.payload.data) {
          return
        }
        state.challenge = action.payload.data
      })

    builder
      .addCase(createNewChallenge.pending, (state) => {
        state.saveStatus = 'loading'
        state.error = null
      })
      .addCase(createNewChallenge.rejected, (state, action) => {
        state.saveStatus = 'failed'
        state.saveAsDraftStatus = 'failed'
        state.error = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(createNewChallenge.fulfilled, (state, action) => {
        state.saveAsDraftStatus = 'succeeded'
        state.saveStatus = 'succeeded'
        if (action.payload.data) {
          state.challenge = action.payload.data
        }
        state.redirectAfterCreate = true
      })

    builder
      .addCase(updateChallenge.pending, (state) => {
        state.saveStatus = 'loading'
        state.error = null
      })
      .addCase(updateChallenge.rejected, (state, action) => {
        state.saveStatus = 'failed'
        state.saveAsDraftStatus = 'failed'
        state.error = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(updateChallenge.fulfilled, (state, action) => {
        state.saveAsDraftStatus = 'succeeded'
        state.saveStatus = 'succeeded'
        if (!action.payload.data) {
          return
        }
        state.challenge = action.payload.data
      })

    builder
      .addCase(updateChallengeState.pending, (state) => {
        state.updateStateStatus = 'loading'
        state.updateStateError = null
      })
      .addCase(updateChallengeState.rejected, (state, action) => {
        state.updateStateStatus = 'failed'
        state.updateStateError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(updateChallengeState.fulfilled, (state, action) => {
        state.updateStateStatus = 'succeeded'
        if (!action.payload.data) {
          return
        }
        state.challenge = action.payload.data
      })

    builder
      .addCase(duplicateChallenge.pending, (state) => {
        state.status = 'loading'
        state.error = null
      })
      .addCase(duplicateChallenge.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(duplicateChallenge.fulfilled, (state) => {
        state.status = 'succeeded'
        state.challenge = challenge
      })

    builder
      .addCase(deleteChallenge.pending, (state) => {
        state.status = 'loading'
        state.error = null
      })
      .addCase(deleteChallenge.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(deleteChallenge.fulfilled, (state) => {
        state.status = 'succeeded'
        state.challenge = challenge
      })

    builder
      .addCase(uploadChallengeImage.pending, (state) => {
        state.fileUploadStatus = 'loading'
        state.fileUploadError = null
      })
      .addCase(uploadChallengeImage.rejected, (state, action) => {
        state.fileUploadStatus = 'failed'
        state.fileUploadError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(uploadChallengeImage.fulfilled, (state, action) => {
        state.fileUploadStatus = 'succeeded'
        const { fileType, filename } = action.payload
        state.challenge[fileType] = filename
      })

    builder
      .addCase(getChallengeLeaderboardReportV1.pending, (state) => {
        state.exportResultsStatus = 'loading'
        state.exportResultsError = null
      })
      .addCase(getChallengeLeaderboardReportV1.rejected, (state, action) => {
        state.exportResultsStatus = 'failed'
        state.exportResultsError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(getChallengeLeaderboardReportV1.fulfilled, (state) => {
        state.exportResultsStatus = 'succeeded'
      })

    builder
      .addCase(deleteChallengePlayer.pending, (state) => {
        state.deleteChallengePlayerStatus = 'loading'
        state.deleteChallengePlayerError = null
      })
      .addCase(deleteChallengePlayer.rejected, (state, action) => {
        state.deleteChallengePlayerStatus = 'failed'
        state.deleteChallengePlayerError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(deleteChallengePlayer.fulfilled, (state) => {
        state.deleteChallengePlayerStatus = 'succeeded'
      })

    builder
      .addCase(addChallengePlayer.pending, (state) => {
        state.addChallengePlayerStatus = 'loading'
        state.addChallengePlayerError = null
      })
      .addCase(addChallengePlayer.rejected, (state, action) => {
        state.addChallengePlayerStatus = 'failed'
        state.addChallengePlayerError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(addChallengePlayer.fulfilled, (state) => {
        state.addChallengePlayerStatus = 'succeeded'
      })
      .addCase(getUserChallenge.pending, (state) => {
        state.status = 'loading'
        state.error = null
      })
      .addCase(getUserChallenge.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(getUserChallenge.fulfilled, (state, action) => {
        state.status = 'succeeded'
        if (!action.payload.data) {
          return
        }
        state.challenge = action.payload.data
      })
  },
})

export const {
  removeImage,
  clearChallenge,
  clearRedirect,
  clearFileUploadStatus,
  clearFileUploadError,
  updateChallengeInfo,
  updateChallengeField,
} = slice.actions

export const selectChallenge = (state: RootState): ChallengeState => state.challenge

export default slice.reducer
